import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PortalShell, PortalMessage, ContractBody } from "@/components/portal/PortalShell";
import { SignaturePad } from "@/components/portal/SignaturePad";

const Signed = ({ signed, token }) => (
  <div className="mt-10 rounded-[24px] border border-emerald-600/30 bg-emerald-50 p-6" data-testid="contract-signed">
    <p className="flex items-center gap-2 font-medium text-emerald-800"><CheckCircle2 className="h-5 w-5" /> Signed</p>
    {signed?.image && <img src={signed.image} alt="Signature" className="mt-4 h-20" />}
    <p className="mt-2 text-sm text-emerald-900">{signed?.typed_name} · {signed?.signed_at ? new Date(signed.signed_at).toLocaleString() : ""}</p>
    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/contracts/${token}/pdf`} className="mt-5 inline-flex items-center gap-2 rounded-full bg-rc-copper px-6 py-3 text-sm font-medium text-white" data-testid="contract-pdf-download">
      <Download className="h-4 w-4" /> Download signed PDF
    </a>
  </div>
);

const SignForm = ({ token, clientName, onSigned }) => {
  const [name, setName] = useState(clientName || "");
  const [agreed, setAgreed] = useState(false);
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/portal/contracts/${token}/sign`, { typed_name: name, agreed, signature: sig });
      onSigned({ typed_name: name, image: sig, signed_at: data?.signed_at });
    } catch (err) { setError(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="mt-10 space-y-5 rounded-[24px] border border-rc-line bg-rc-surface p-6 sm:p-8" data-testid="sign-form">
      <p className="font-display text-3xl">Sign this contract</p>
      <label className="block">
        <span className="font-mono-rc text-[11px] uppercase tracking-[0.2em] text-rc-muted">Type your full legal name</span>
        <input required value={name} onChange={(e) => setName(e.target.value)} className="mt-2 w-full rounded-xl border border-rc-line2 bg-white px-4 py-3 outline-none focus:border-rc-copper" data-testid="sign-name-input" />
      </label>
      <div>
        <span className="mb-2 block font-mono-rc text-[11px] uppercase tracking-[0.2em] text-rc-muted">Draw your signature</span>
        <SignaturePad onChange={setSig} />
      </div>
      <label className="flex items-start gap-3 text-sm text-rc-body">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-4 w-4 accent-[#9A5E17]" data-testid="sign-agree-checkbox" />
        I have read and agree to this contract, and I understand that my typed name and drawn signature are my electronic signature.
      </label>
      {error && <p className="text-sm text-red-600" data-testid="sign-error">{error}</p>}
      <button type="submit" disabled={busy || !agreed || !sig || name.trim().length < 2} className="flex items-center gap-2 rounded-full bg-rc-copper px-7 py-3 font-medium text-white transition-[opacity] disabled:opacity-40" data-testid="sign-submit-btn">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign contract
      </button>
    </form>
  );
};

export default function ContractPortal() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    api.get(`/portal/contracts/${token}`)
      .then((r) => setState({ data: r.data }))
      .catch((e) => setState({ error: formatApiError(e) }));
  }, [token]);

  const d = state.data;
  return (
    <PortalShell testId="contract-portal">
      {state.loading && <Loader2 className="mx-auto h-6 w-6 animate-spin text-rc-copper" />}
      {state.error && <PortalMessage title="Link unavailable" text={state.error} />}
      {d && (
        <>
          <p className="font-mono-rc text-xs uppercase tracking-[0.25em] text-rc-copper">Contract for {d.client_name}</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl" data-testid="contract-title">{d.title}</h1>
          <div className="mt-8 rounded-[24px] border border-rc-line bg-rc-surface p-6 sm:p-10"><ContractBody body={d.body} /></div>
          {d.status === "signed" ? <Signed signed={d.signed} token={token} /> : (
            <SignForm token={token} clientName={d.client_name} onSigned={(signed) => setState({ data: { ...d, status: "signed", signed } })} />
          )}
        </>
      )}
    </PortalShell>
  );
}
